import React, { createContext, useContext, useState, useEffect } from 'react';
import { useAuth } from './AuthContext';
import { creditsApi, authApi } from '../services/api';

const CreditsContext = createContext();

export function useCredits() {
  return useContext(CreditsContext);
}

export function CreditsProvider({ children }) {
  const { currentUser, isAnonymous } = useAuth();
  const [credits, setCredits] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Fetch credits whenever user auth state changes
  useEffect(() => {
    const fetchCredits = async () => {
      if (!currentUser) {
        setCredits(0);
        setLoading(false);
        return;
      }

      try {
        setLoading(true);
        setError(null);
        
        const response = await creditsApi.getCredits();
        
        if (response.success) {
          setCredits(response.data.credits);
        } else {
          setError('Failed to fetch credits');
        }
      } catch (err) {
        console.error('Error fetching credits:', err);
        setError('Failed to fetch credits');
      } finally {
        setLoading(false);
      }
    };

    fetchCredits();
  }, [currentUser]);

  // Register user with backend when they sign up
  const registerUserWithBackend = async (displayName) => {
    try {
      setLoading(true);
      setError(null);
      
      const response = await authApi.registerUser(displayName);
      
      if (response.success) {
        setCredits(response.data.credits);
        return response.data;
      } else {
        setError('Failed to register user with backend');
        throw new Error('Failed to register user with backend');
      }
    } catch (err) {
      console.error('Error registering user with backend:', err);
      setError(err.message || 'Failed to register user with backend');
      throw err;
    } finally {
      setLoading(false);
    }
  };

  // Use credits for a service
  const useCredits = async (amount) => {
    if (isAnonymous) {
      setError('You need to be logged in to use credits');
      return { success: false, message: 'You need to be logged in to use credits' };
    }
    
    if (credits < amount) {
      setError('Insufficient credits');
      return { success: false, message: 'Insufficient credits' };
    }
    
    try {
      setLoading(true);
      setError(null);
      
      const response = await creditsApi.deductCredits(amount);
      
      if (response.success) {
        setCredits(response.data.credits);
        return { success: true, credits: response.data.credits };
      } else {
        setError('Failed to use credits');
        return { success: false, message: 'Failed to use credits' };
      }
    } catch (err) {
      console.error('Error using credits:', err);
      setError(err.message || 'Failed to use credits');
      return { success: false, message: err.message || 'Failed to use credits' };
    } finally {
      setLoading(false);
    }
  };

  const value = {
    credits,
    loading,
    error,
    registerUserWithBackend,
    useCredits,
    setCredits,
  };

  return (
    <CreditsContext.Provider value={value}>
      {children}
    </CreditsContext.Provider>
  );
} 